import { useState, useCallback, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Search, FileText } from 'lucide-react'
import {
  useOrgArtefactTemplates,
  useArtefactTemplateMutations,
} from '../hooks/use-artefact-templates'
import { VelociraptorConfigModal } from '../components/velociraptor/VelociraptorConfigModal'
import { OrgTemplateCard } from '../components/velociraptor/OrgTemplateCard'
import { TestBuildDialog } from '../components/velociraptor/TestBuildDialog'
import { CollectorSettingsDialog } from '../components/velociraptor/CollectorSettingsDialog'
import { ActiveDeploymentWarningDialog } from '../components/velociraptor/ActiveDeploymentWarningDialog'
import type { ArtefactTemplate, TemplateActiveUsage } from '../types/artefact-templates'

interface DialogTarget {
  id: string
  name: string
}

interface TemplateDialogState {
  configModalOpen: boolean
  editTemplate: ArtefactTemplate | null
  testBuild: DialogTarget | null
  settings: DialogTarget | null
  warningOpen: boolean
  pendingEdit: ArtefactTemplate | null
  warningUsage: TemplateActiveUsage | null
  openNew: () => void
  openEdit: (tpl: ArtefactTemplate) => void
  onSaved: () => void
  closeConfig: () => void
  openTestBuild: (tpl: ArtefactTemplate) => void
  openSettings: (tpl: ArtefactTemplate) => void
  setTestBuild: (val: DialogTarget | null) => void
  setSettings: (val: DialogTarget | null) => void
  setWarningOpen: (val: boolean) => void
  setPendingEdit: (val: ArtefactTemplate | null) => void
}

function useTemplateDialogs(refetch: () => Promise<void>): TemplateDialogState {
  const [configModalOpen, setConfigModalOpen] = useState(false)
  const [editTemplate, setEditTemplate] = useState<ArtefactTemplate | null>(null)
  const [testBuild, setTestBuild] = useState<DialogTarget | null>(null)
  const [settings, setSettings] = useState<DialogTarget | null>(null)
  const [warningOpen, setWarningOpen] = useState(false)
  const [pendingEdit, setPendingEdit] = useState<ArtefactTemplate | null>(null)
  const [warningUsage] = useState<TemplateActiveUsage | null>(null)

  const openNew = useCallback(() => {
    setEditTemplate(null)
    setConfigModalOpen(true)
  }, [])
  const openEdit = useCallback((tpl: ArtefactTemplate) => {
    setEditTemplate(tpl)
    setConfigModalOpen(true)
  }, [])
  const onSaved = useCallback(() => {
    setConfigModalOpen(false)
    setEditTemplate(null)
    void refetch()
  }, [refetch])
  const closeConfig = useCallback(() => {
    setConfigModalOpen(false)
  }, [])
  const openTestBuild = useCallback((tpl: ArtefactTemplate) => {
    setTestBuild({ id: tpl.id, name: tpl.name })
  }, [])
  const openSettings = useCallback((tpl: ArtefactTemplate) => {
    setSettings({ id: tpl.id, name: tpl.name })
  }, [])

  return {
    configModalOpen,
    editTemplate,
    testBuild,
    settings,
    warningOpen,
    pendingEdit,
    warningUsage,
    openNew,
    openEdit,
    onSaved,
    closeConfig,
    openTestBuild,
    openSettings,
    setTestBuild,
    setSettings,
    setWarningOpen,
    setPendingEdit,
  }
}

export function OrgVelociraptorTemplatesPage(): ReactElement {
  const { templates, loading, error, refetch } = useOrgArtefactTemplates()
  const mutations = useArtefactTemplateMutations()
  const [searchQuery, setSearchQuery] = useState('')
  const [platformFilter, setPlatformFilter] = useState<string>('')
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const dialogs = useTemplateDialogs(refetch)

  const filteredTemplates = filterTemplates({ templates, searchQuery, platformFilter })

  async function handleDelete(templateId: string): Promise<void> {
    const success = await mutations.deleteOrg(templateId)
    if (success) {
      setDeleteConfirmId(null)
      void refetch()
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center" role="status">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#4e5e82] border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader templateCount={templates.length} onNewTemplate={dialogs.openNew} />

      {error && (
        <div
          className="rounded-lg bg-red-50 p-4 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300"
          role="alert"
        >
          {error}
        </div>
      )}

      <SearchAndFilter
        searchQuery={searchQuery}
        platformFilter={platformFilter}
        onSearchChange={setSearchQuery}
        onPlatformChange={setPlatformFilter}
      />

      {filteredTemplates.length === 0 ? (
        <EmptyState hasTemplates={templates.length > 0} onNewTemplate={dialogs.openNew} />
      ) : (
        <TemplateList
          templates={filteredTemplates}
          deleteConfirmId={deleteConfirmId}
          onEdit={dialogs.openEdit}
          onDelete={setDeleteConfirmId}
          onDeleteConfirm={(id) => {
            void handleDelete(id)
          }}
          onDeleteCancel={() => {
            setDeleteConfirmId(null)
          }}
          onTestBuild={dialogs.openTestBuild}
          onCollectorSettings={dialogs.openSettings}
          onSaveAsOrg={dialogs.openEdit}
        />
      )}

      <TemplateDialogs dialogs={dialogs} refetch={refetch} />
    </div>
  )
}

function TemplateDialogs({
  dialogs,
  refetch,
}: {
  dialogs: ReturnType<typeof useTemplateDialogs>
  refetch: () => Promise<void>
}): ReactElement {
  return (
    <>
      <VelociraptorConfigModal
        isOpen={dialogs.configModalOpen}
        onClose={dialogs.closeConfig}
        onTemplateSaved={dialogs.onSaved}
        editTemplate={dialogs.editTemplate}
        context={{ scope: 'organisation', userRole: 'org_admin' }}
      />
      <TestBuildDialog
        isOpen={dialogs.testBuild !== null}
        templateId={dialogs.testBuild?.id ?? ''}
        templateName={dialogs.testBuild?.name ?? ''}
        onClose={() => {
          dialogs.setTestBuild(null)
        }}
        onBuildComplete={() => {
          void refetch()
        }}
      />
      <CollectorSettingsDialog
        isOpen={dialogs.settings !== null}
        templateId={dialogs.settings?.id ?? ''}
        templateName={dialogs.settings?.name ?? ''}
        onClose={() => {
          dialogs.setSettings(null)
        }}
        onSaved={() => {
          void refetch()
        }}
      />
      <ActiveDeploymentWarningDialog
        isOpen={dialogs.warningOpen}
        usage={dialogs.warningUsage ?? { activeRequestCount: 0, activeCaseCount: 0 }}
        onProceed={() => {
          dialogs.setWarningOpen(false)
          if (dialogs.pendingEdit) {
            dialogs.openEdit(dialogs.pendingEdit)
            dialogs.setPendingEdit(null)
          }
        }}
        onCancel={() => {
          dialogs.setWarningOpen(false)
          dialogs.setPendingEdit(null)
        }}
      />
    </>
  )
}

function PageHeader({
  templateCount,
  onNewTemplate,
}: {
  templateCount: number
  onNewTemplate: () => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-slate-800 dark:text-slate-100">
          {t('velociraptor.orgTemplates.pageTitle')}
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {t('velociraptor.orgTemplates.pageDescription', { count: templateCount })}
        </p>
      </div>
      <button
        onClick={onNewTemplate}
        className="flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] focus:ring-2 focus:ring-[#4e5e82] focus:outline-none"
      >
        <Plus className="h-4 w-4" />
        {t('velociraptor.orgTemplates.newTemplate')}
      </button>
    </div>
  )
}

function SearchAndFilter({
  searchQuery,
  platformFilter,
  onSearchChange,
  onPlatformChange,
}: {
  searchQuery: string
  platformFilter: string
  onSearchChange: (value: string) => void
  onPlatformChange: (value: string) => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex gap-4">
      <div className="relative flex-1">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(ev) => {
            onSearchChange(ev.target.value)
          }}
          placeholder={t('velociraptor.orgTemplates.searchPlaceholder')}
          className="w-full rounded-lg border border-slate-200 bg-white py-2 pr-4 pl-10 text-sm text-slate-900 placeholder-slate-400 focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
        />
      </div>
      <select
        value={platformFilter}
        onChange={(ev) => {
          onPlatformChange(ev.target.value)
        }}
        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
      >
        <option value="">{t('velociraptor.orgTemplates.allPlatforms')}</option>
        <option value="windows">{t('velociraptor.orgTemplates.platformWindows')}</option>
        <option value="linux">{t('velociraptor.orgTemplates.platformLinux')}</option>
        <option value="macos">{t('velociraptor.orgTemplates.platformMacOS')}</option>
      </select>
    </div>
  )
}

function EmptyState({
  hasTemplates,
  onNewTemplate,
}: {
  hasTemplates: boolean
  onNewTemplate: () => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 dark:border-slate-700">
      <FileText className="h-12 w-12 text-slate-300 dark:text-slate-600" />
      <h3 className="font-heading mt-4 text-lg font-semibold text-slate-700 dark:text-slate-300">
        {hasTemplates
          ? t('velociraptor.orgTemplates.noResults')
          : t('velociraptor.orgTemplates.emptyTitle')}
      </h3>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {hasTemplates
          ? t('velociraptor.orgTemplates.noResultsDescription')
          : t('velociraptor.orgTemplates.emptyDescription')}
      </p>
      {!hasTemplates && (
        <button
          onClick={onNewTemplate}
          className="mt-4 flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] focus:ring-2 focus:ring-[#4e5e82] focus:outline-none"
        >
          <Plus className="h-4 w-4" />
          {t('velociraptor.orgTemplates.newTemplate')}
        </button>
      )}
    </div>
  )
}

interface TemplateListProps {
  templates: ArtefactTemplate[]
  deleteConfirmId: string | null
  onEdit: (template: ArtefactTemplate) => void
  onDelete: (templateId: string) => void
  onDeleteConfirm: (templateId: string) => void
  onDeleteCancel: () => void
  onTestBuild: (template: ArtefactTemplate) => void
  onCollectorSettings: (template: ArtefactTemplate) => void
  onSaveAsOrg: (template: ArtefactTemplate) => void
}

function TemplateList(props: TemplateListProps): ReactElement {
  const { t } = useTranslation('cases')
  const orgTemplates = props.templates.filter((tpl) => tpl.scope === 'organisation')
  const systemTemplates = props.templates.filter((tpl) => tpl.scope === 'system')
  const caseTemplates = props.templates.filter((tpl) => tpl.scope === 'case')

  return (
    <div className="space-y-6">
      {orgTemplates.length > 0 && (
        <TemplateSection
          label={t('velociraptor.orgTemplates.orgSection')}
          templates={orgTemplates}
          listProps={props}
        />
      )}
      {caseTemplates.length > 0 && (
        <TemplateSection
          label={t('velociraptor.orgTemplates.caseTemplatesSection')}
          templates={caseTemplates}
          listProps={props}
        />
      )}
      {systemTemplates.length > 0 && (
        <TemplateSection
          label={t('velociraptor.orgTemplates.systemSection')}
          templates={systemTemplates}
          listProps={props}
        />
      )}
    </div>
  )
}

function TemplateSection({
  label,
  templates,
  listProps,
}: {
  label: string
  templates: ArtefactTemplate[]
  listProps: TemplateListProps
}): ReactElement {
  return (
    <div className="space-y-3">
      <h4 className="font-heading text-xs font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
        {label}
      </h4>
      {templates.map((template) => (
        <TemplateSectionCard key={template.id} template={template} listProps={listProps} />
      ))}
    </div>
  )
}

function TemplateSectionCard({
  template,
  listProps,
}: {
  template: ArtefactTemplate
  listProps: TemplateListProps
}): ReactElement {
  const isSystem = template.scope === 'system'
  const isCaseTemplate = template.scope === 'case'
  const baseProps = {
    template,
    isSystem,
    isCaseTemplate,
    deleteConfirm: listProps.deleteConfirmId === template.id,
    onDeleteConfirm: () => {
      listProps.onDeleteConfirm(template.id)
    },
    onDeleteCancel: listProps.onDeleteCancel,
    onTestBuild: () => {
      listProps.onTestBuild(template)
    },
  }
  if (isCaseTemplate || isSystem) {
    return (
      <OrgTemplateCard
        {...baseProps}
        onSaveAsOrg={() => {
          listProps.onSaveAsOrg(template)
        }}
      />
    )
  }
  return (
    <OrgTemplateCard
      {...baseProps}
      onEdit={() => {
        listProps.onEdit(template)
      }}
      onDelete={() => {
        listProps.onDelete(template.id)
      }}
      onCollectorSettings={() => {
        listProps.onCollectorSettings(template)
      }}
    />
  )
}

function filterTemplates({
  templates,
  searchQuery,
  platformFilter,
}: {
  templates: ArtefactTemplate[]
  searchQuery: string
  platformFilter: string
}): ArtefactTemplate[] {
  const query = searchQuery.toLowerCase().trim()
  return templates.filter((template) => {
    if (
      query &&
      !template.name.toLowerCase().includes(query) &&
      !template.description?.toLowerCase().includes(query)
    ) {
      return false
    }
    if (platformFilter && !template.supportedPlatforms.includes(platformFilter)) {
      return false
    }
    return true
  })
}
